import { useState, useCallback, useRef, useEffect } from 'react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { ReviewRun } from './types';

export type JobStatus = 'pending' | 'running' | 'done' | 'failed' | null;

export function useClaudeReview(entityRef: string, repoSlug: string) {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [run, setRun] = useState<ReviewRun | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatus>(null);
  const [isFullScan, setIsFullScan] = useState(false);

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const baseUrlRef = useRef<string>('');

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => {
    return stopPolling;
  }, [stopPolling]);

  const pollJob = useCallback(
    async (jobId: string) => {
      try {
        const resp = await fetchApi.fetch(`${baseUrlRef.current}/jobs/${jobId}`);
        if (!resp.ok) throw new Error(`Poll failed: ${resp.status}`);
        const data = await resp.json();
        const { job, run: completedRun } = data;
        setJobStatus(job.status);
        if (job.status === 'done') {
          setRun(completedRun);
          setLoading(false);
          stopPolling();
        } else if (job.status === 'failed') {
          setError(job.error ?? 'Analysis failed');
          setLoading(false);
          stopPolling();
        } else {
          pollRef.current = setTimeout(() => pollJob(jobId), 3000);
        }
      } catch (e: any) {
        setError(e.message);
        setLoading(false);
        stopPolling();
      }
    },
    [fetchApi, stopPolling],
  );

  const fetchLatest = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('claude-review');
      const encodedRef = encodeURIComponent(entityRef);
      const resp = await fetchApi.fetch(`${baseUrl}/runs/${encodedRef}`);
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error?.message ?? err.error ?? `Server error ${resp.status}`);
      }
      const data = await resp.json();
      setRun(data.run ?? null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, fetchApi, entityRef]);

  const runAnalysis = useCallback(
    async (fullScan = false) => {
      stopPolling();
      setLoading(true);
      setError(null);
      setJobStatus(null);
      setIsFullScan(fullScan);
      try {
        const baseUrl = await discoveryApi.getBaseUrl('claude-review');
        baseUrlRef.current = baseUrl;
        const resp = await fetchApi.fetch(`${baseUrl}/analyze`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ entityRef, repoSlug, fullScan }),
        });
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.detail ?? err.error ?? 'Analysis failed');
        }
        const data = await resp.json();
        setJobStatus('pending');
        pollRef.current = setTimeout(() => pollJob(data.jobId), 2000);
      } catch (e: any) {
        setError(e.message);
        setLoading(false);
      }
    },
    [discoveryApi, fetchApi, entityRef, repoSlug, pollJob, stopPolling],
  );

  return { run, loading, error, jobStatus, isFullScan, fetchLatest, runAnalysis };
}
